import axios, { AxiosRequestConfig } from 'axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ITokenResponse {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
}

export interface IDecodedIdToken {
    oracleID: string;
    fname: string;
    lname: string;
    email: string;
    userManagerLevel: string;
    memberOf: string[];
    sub: string;
    exp: number;
    [key: string]: unknown;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const getTokenRequestHeaders = (): AxiosRequestConfig => ({
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
    },
});

const decodeIdToken = (token: string): IDecodedIdToken => {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload)) as IDecodedIdToken;
};

// ── Auth request ──────────────────────────────────────────────────────────────

export const sendAuthorizationRequest = (): void => {
    // URLSearchParams key names must match the OAuth spec (snake_case).
    // The camelcase ESLint rule applies to JS identifiers, not string keys,
    // so using them as object literals passed directly to URLSearchParams is safe.
    const params = new URLSearchParams();
    params.append('response_type', process.env.REACT_APP_RESPONSE_TYPE ?? '');
    params.append('scope', process.env.REACT_APP_SCOPE ?? '');
    params.append('redirect_uri', process.env.REACT_APP_REDIRECT_URI ?? '');
    params.append('client_id', process.env.REACT_APP_CLIENT_ID ?? '');

    window.location.href = `${process.env.REACT_APP_PING_AUTH_URI}?${params.toString()}`;
};

// ── Token request ─────────────────────────────────────────────────────────────

export const sendTokenRequest = (
    code: string,
): Promise<[ITokenResponse, IDecodedIdToken]> => {
    const body = new URLSearchParams();
    body.append('client_id', process.env.REACT_APP_CLIENT_ID ?? '');
    body.append('code', code);
    body.append('grant_type', process.env.REACT_APP_GRANT_TYPE ?? '');
    body.append('redirect_uri', process.env.REACT_APP_REDIRECT_URI ?? '');

    return axios
        .post<ITokenResponse>(
            process.env.REACT_APP_PING_TOKEN_URI ?? '',
            body.toString(),
            getTokenRequestHeaders(),
        )
        .then((response) => [response.data, decodeIdToken(response.data.id_token)]);
        // .then((response) => [response.data, decodeIdToken(response.data.access_token)]);
};

export interface HeartbeatResult {
    success: boolean;
    errorCode?: string;
    httpStatus?: number;
    latencyMs: number;
    isNetworkBlock: boolean;
}

export const checkHeartbeat = async (): Promise<HeartbeatResult> => {
    const start = Date.now();
    const backendUrl = process.env.REACT_APP_API_URI ?? '';
    try {
        await axios.get(backendUrl, { timeout: 5000 });
        return { success: true, latencyMs: Date.now() - start, isNetworkBlock: false };
    } catch (error: any) {
        const latencyMs = Date.now() - start;
        const hasResponse = !!error.response;
        return {
            success: false,
            errorCode: error.code,
            httpStatus: error.response?.status,
            latencyMs,
            isNetworkBlock: !hasResponse,
        };
    }
};

// ── Refresh token request ─────────────────────────────────────────────────────

export const sendRefreshTokenRequest = (
    refreshToken: string,
): Promise<[ITokenResponse, IDecodedIdToken]> => {
    const body = new URLSearchParams();
    body.append('grant_type', process.env.REACT_APP_REFRESH_GRANT_TYPE ?? '');
    body.append('client_id', process.env.REACT_APP_CLIENT_ID ?? '');
    body.append('redirect_uri', process.env.REACT_APP_REDIRECT_URI ?? '');
    body.append('refresh_token', refreshToken);

    return axios
        .post<ITokenResponse>(
            process.env.REACT_APP_PING_TOKEN_URI ?? '',
            body.toString(),
            getTokenRequestHeaders(),
        )
        .then((response) => [response.data, decodeIdToken(response.data.id_token)]);
        // .then((response) => [response.data, decodeIdToken(response.data.access_token)]);
};
